/* ===== RESET & BASE ===== */
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
html { scroll-behavior: smooth; }
body {
  font-family: 'Segoe UI', 'Cairo', Tahoma, Geneva, sans-serif;
  background: var(--bg);
  color: var(--text);
  transition: background 0.3s, color 0.3s;
  min-height: 100vh;
}

/* ===== CSS VARIABLES ===== */
:root {
  --primary: #1a4a7a;
  --primary-dark: #0f2d4f;
  --primary-light: #2563a8;
  --accent: #e87722;
  --accent-dark: #c5601a;
  --accent-light: #f59340;
  --bg: #f7f8fa;
  --bg-card: #ffffff;
  --bg-section: #eef2f7;
  --text: #1a2332;
  --text-muted: #5a6a7e;
  --text-light: #8a9ab0;
  --border: #d0dae7;
  --shadow: 0 4px 24px rgba(26,74,122,0.10);
  --shadow-lg: 0 12px 48px rgba(26,74,122,0.16);
  --radius: 14px;
  --radius-sm: 8px;

  /* ===== LOGO-DERIVED NAVY COLOR SYSTEM =====
     Extracted from logo.png deep navy #023151 (h≈204°, s≈95%, l≈16%).
     Constrained 9-shade scale per refactoring-ui principle #4.
  */
  --navy-50: #f0f4f8;
  --navy-100: #dbe3ee;
  --navy-200: #b9cbd8;
  --navy-300: #8da9bd;
  --navy-400: #5e809c;
  --navy-500: #023151;
  --navy-600: #02253d;
  --navy-700: #02192a;
  --navy-800: #01101b;
  --navy-900: #00080d;

  /* Neutral text scale (grayscale-first hierarchy) */
  --ink-dark: #0f172a;
  --ink-base: #334155;
  --ink-muted: #64748b;
  --ink-light: #94a3b8;

  /* Depth system */
  --shadow-sm: 0 1px 3px rgba(2,49,81,0.08);
  --shadow-md: 0 4px 12px rgba(2,49,81,0.10);
  --shadow-lg: 0 12px 40px rgba(2,49,81,0.14);
  --shadow-xl: 0 20px 60px rgba(2,49,81,0.20);

  /* Spacing scale (refactoring-ui #2): 4,8,16,24,32,48,64 */
  --space-1: 4px;
  --space-2: 8px;
  --space-3: 12px;
  --space-4: 16px;
  --space-5: 20px;
  --space-6: 24px;
  --space-8: 32px;
  --space-10: 40px;
  --space-12: 48px;
  --space-16: 64px;

  --radius: 16px;
  --radius-lg: 24px;
  --navbar-h: 72px;
}
.dark {
  --bg: #0f1923;
  --bg-card: #1a2638;
  --bg-section: #152030;
  --text: #e8edf4;
  --text-muted: #8fa5c0;
  --text-light: #5a7a9a;
  --border: #253547;
  --shadow: 0 4px 24px rgba(0,0,0,0.30);
  --shadow-lg: 0 12px 48px rgba(0,0,0,0.40);
}

/* ===== UTILITY ===== */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 28px;
  border-radius: 50px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  text-decoration: none;
  transition: all 0.22s ease;
}
.btn-primary { background: var(--accent); color: #fff; }
.btn-primary:hover { background: var(--accent-dark); transform: translateY(-2px); box-shadow: 0 6px 20px rgba(232,119,34,0.35); }
.btn-outline { background: transparent; color: #fff; border: 2px solid rgba(255,255,255,0.7); }
.btn-outline:hover { background: rgba(255,255,255,0.15); }
.btn-white { background: #fff; color: var(--primary); font-weight: 700; }
.btn-white:hover { transform: translateY(-2px); box-shadow: var(--shadow); }

/* ===== NAVBAR ===== */
.navbar {
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 999;
  background: rgba(15, 29, 47, 0.97);
  backdrop-filter: blur(12px);
  box-shadow: 0 2px 20px rgba(0,0,0,0.18);
  height: var(--navbar-h);
}
.nav-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  height: 100%;
  display: flex;
  align-items: center;
  gap: 24px;
}
.nav-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  flex-shrink: 0;
}
.logo-img { width: 46px; height: 46px; object-fit: contain; border-radius: 8px; }
.logo-text { display: flex; flex-direction: column; line-height: 1.1; }
.logo-main { color: #fff; font-size: 18px; font-weight: 800; }
.logo-sub { color: var(--accent-light); font-size: 11px; font-weight: 500; }
.nav-links { display: flex; list-style: none; gap: 4px; margin: 0 auto; }
.nav-link {
  color: rgba(255,255,255,0.82);
  text-decoration: none;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
}
.nav-link:hover, .nav-link.active { color: #fff; background: rgba(255,255,255,0.1); }
.nav-link.active { color: var(--accent-light); }
.nav-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.nav-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s;
  background: rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.85);
}
.nav-btn:hover { background: rgba(255,255,255,0.15); color: #fff; }
.icon-btn { padding: 8px 10px; }
.lang-btn { border: 1px solid rgba(255,255,255,0.2); }
.chat-btn { background: var(--accent); color: #fff; padding: 8px 14px; }
.chat-btn:hover { background: var(--accent-dark); }
.mobile-menu-btn { display: none; background: transparent; border: none; color: #fff; cursor: pointer; padding: 6px; }
.nav-overlay { display: none; }

@media (max-width: 768px) {
  .nav-links {
    position: absolute;
    top: 100%;
    left: 0; right: 0;
    background: #0f1d2f;
    flex-direction: column;
    padding: 0 16px;
    gap: 4px;
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.3s ease, padding 0.3s ease;
    z-index: 998;
    box-shadow: 0 8px 24px rgba(0,0,0,0.3);
  }
  .nav-links.open {
    max-height: 400px;
    padding: 12px 16px;
  }
  .nav-overlay { display: block; position: fixed; inset: 0; z-index: 997; top: var(--navbar-h); }
  .mobile-menu-btn { display: flex; }
  .chat-label { display: none; }
  .lang-btn span { display: none; }
}

/* ===== PAGE BASE ===== */
.page { padding-top: var(--navbar-h); }

/* ===== SECTION ===== */
.section { padding: 80px 0; }
.section:nth-child(even) { background: var(--bg-section); }
.section-inner { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
.section-header { text-align: center; margin-bottom: 56px; }
.section-header h2 { font-size: clamp(26px, 3.5vw, 36px); color: var(--primary); font-weight: 800; margin-bottom: 12px; }
.dark .section-header h2 { color: var(--accent-light); }
.section-header p { color: var(--text-muted); font-size: 17px; max-width: 560px; margin: 0 auto; }

/* ===== HERO ===== */
.hero-section { position: relative; min-height: 95vh; display: flex; align-items: center; overflow: hidden; }
.hero-bg { position: absolute; inset: 0; }
.hero-bg-img { width: 100%; height: 100%; object-fit: cover; }
.hero-overlay {
  position: absolute; inset: 0;
  background: linear-gradient(135deg, rgba(10,28,50,0.92) 0%, rgba(26,74,122,0.75) 60%, rgba(232,119,34,0.25) 100%);
}
.hero-content {
  position: relative;
  z-index: 2;
  max-width: 720px;
  padding: 60px 5vw;
}
.hero-tagline {
  color: var(--accent-light);
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 3px;
  text-transform: uppercase;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.hero-tagline::before { content: ''; display: inline-block; width: 32px; height: 2px; background: var(--accent); }
.hero-title { color: var(--accent-light); font-size: clamp(34px, 5vw, 60px); font-weight: 900; line-height: 1.15; margin-bottom: 20px; }
.hero-desc { color: var(--accent-light); font-size: 20px; font-weight: 500; line-height: 1.8; margin-bottom: 36px; max-width: 560px; }
.hero-btns { display: flex; gap: 16px; flex-wrap: wrap; }

/* ===== SERVICES ===== */
.services-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 24px; }
.service-card {
  background: var(--bg-card);
  border-radius: var(--radius);
  padding: 36px 28px;
  box-shadow: var(--shadow);
  border: 1px solid var(--border);
  transition: transform 0.25s, box-shadow 0.25s;
  text-align: center;
}
.service-card:hover { transform: translateY(-6px); box-shadow: var(--shadow-lg); }
.service-icon {
  width: 72px; height: 72px;
  border-radius: 16px;
  background: linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%);
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 20px;
  color: #fff;
}
.service-card h3 { font-size: 18px; font-weight: 700; color: var(--text); margin-bottom: 12px; }
.service-card p { color: var(--text-muted); font-size: 15px; line-height: 1.7; }

/* ===== PORTS ===== */
.ports-section { background: var(--bg-section) !important; }
.ports-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 24px; }
.port-card { border-radius: var(--radius); overflow: hidden; box-shadow: var(--shadow); background: var(--bg-card); transition: transform 0.25s, box-shadow 0.25s; }
.port-card:hover { transform: translateY(-6px); box-shadow: var(--shadow-lg); }
.port-img-wrap { position: relative; height: 200px; overflow: hidden; }
.port-img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.4s; }
.port-card:hover .port-img { transform: scale(1.06); }
.port-img-overlay { position: absolute; inset: 0; background: linear-gradient(to bottom, transparent 40%, rgba(10,28,50,0.7)); }
.port-info { padding: 24px; }
.port-icon { color: var(--accent); margin-bottom: 10px; }
.port-info h3 { font-size: 18px; font-weight: 700; color: var(--text); margin-bottom: 8px; }
.port-info p { color: var(--text-muted); font-size: 14px; line-height: 1.6; }

/* ===== FREE ZONES ===== */
.freezones-section { background: var(--bg-section) !important; }
.freezones-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 24px; }
.freezone-card {
  background: var(--bg-card); border-radius: var(--radius); padding: 32px 24px;
  box-shadow: var(--shadow); border: 1px solid var(--border);
  text-align: center; transition: transform 0.25s, box-shadow 0.25s;
}
.freezone-card:hover { transform: translateY(-6px); box-shadow: var(--shadow-lg); }
.freezone-icon {
  width: 64px; height: 64px; border-radius: 14px;
  background: linear-gradient(135deg, var(--primary), var(--accent));
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 16px; color: #fff;
}
.freezone-card h3 { font-size: 17px; font-weight: 700; color: var(--text); margin-bottom: 10px; }
.freezone-card p { color: var(--text-muted); font-size: 14px; line-height: 1.6; }

/* ===== CLIENTS ===== */
.clients-section { background: var(--bg) !important; }
.clients-grid {
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 32px;
  max-width: 720px; margin: 0 auto;
}
@media (max-width: 600px) { .clients-grid { grid-template-columns: 1fr; } }
.client-card {
  background: var(--bg-card); border-radius: var(--radius); padding: 40px 28px;
  box-shadow: var(--shadow); border: 1px solid var(--border);
  text-align: center; transition: transform 0.25s, box-shadow 0.25s;
}
.client-card:hover { transform: translateY(-6px); box-shadow: var(--shadow-lg); }
.client-icon {
  width: 80px; height: 80px; border-radius: 50%;
  background: linear-gradient(135deg, var(--primary), var(--accent));
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 20px; color: #fff; padding: 16px;
}
.client-card h3 { font-size: 20px; font-weight: 700; color: var(--text); margin-bottom: 12px; }
.client-card p { color: var(--text-muted); font-size: 15px; line-height: 1.7; }

.clients-home-cta { text-align: center; margin-top: 40px; }

.clients-marquee-wrap {
  overflow: hidden;
  mask-image: linear-gradient(to right, transparent, #000 5%, #000 95%, transparent);
  -webkit-mask-image: linear-gradient(to right, transparent, #000 5%, #000 95%, transparent);
}
.clients-marquee-wrap:hover .clients-marquee-track {
  animation-play-state: paused;
}
.clients-marquee-track {
  display: flex;
  gap: 48px;
  width: max-content;
  animation: marquee-scroll 120s linear infinite;
}
.clients-marquee-group {
  display: flex;
  gap: 48px;
  align-items: center;
}
.clients-marquee-logo {
  height: 52px;
  width: auto;
  max-width: 140px;
  object-fit: contain;
  filter: grayscale(1) brightness(1.1);
  opacity: 0.7;
  transition: opacity 0.25s, filter 0.25s;
  flex-shrink: 0;
}
.clients-marquee-logo:hover {
  opacity: 1;
  filter: none;
}
.clients-marquee-name {
  white-space: nowrap;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-muted);
  opacity: 0.8;
  transition: opacity 0.25s, color 0.25s;
  flex-shrink: 0;
}
.clients-marquee-name:hover {
  opacity: 1;
  color: var(--primary);
}
@keyframes marquee-scroll {
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}

/* ===== LAND PORTS ===== */
.landports-section { background: var(--bg-section) !important; }
.landports-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 20px; }
.landport-card {
  display: flex; align-items: flex-start; gap: 16px;
  background: var(--bg-card); border-radius: var(--radius); padding: 24px;
  box-shadow: var(--shadow); border: 1px solid var(--border);
  transition: transform 0.25s, box-shadow 0.25s;
}
.landport-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-lg); }
.landport-icon {
  width: 52px; height: 52px; border-radius: 12px; flex-shrink: 0;
  background: linear-gradient(135deg, var(--primary), var(--accent));
  display: flex; align-items: center; justify-content: center; color: #fff;
}
.landport-info h3 { font-size: 16px; font-weight: 700; color: var(--text); margin-bottom: 6px; }
.landport-info p { color: var(--text-muted); font-size: 13px; line-height: 1.6; }

/* ===== SEA PORTS ===== */
.seaports-section { background: var(--bg-section) !important; }
.seaports-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 20px; }
.seaport-card {
  display: flex; align-items: flex-start; gap: 16px;
  background: var(--bg-card); border-radius: var(--radius); padding: 24px;
  box-shadow: var(--shadow); border: 1px solid var(--border);
  transition: transform 0.25s, box-shadow 0.25s;
}
.seaport-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-lg); }
.seaport-icon {
  width: 52px; height: 52px; border-radius: 12px; flex-shrink: 0;
  background: linear-gradient(135deg, var(--primary), var(--accent));
  display: flex; align-items: center; justify-content: center; color: #fff;
}
.seaport-info h3 { font-size: 16px; font-weight: 700; color: var(--text); margin-bottom: 6px; }
.seaport-info p { color: var(--text-muted); font-size: 13px; line-height: 1.6; }

/* ===== WHY US ===== */
.why-section { background: var(--bg) !important; }
.why-inner { display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: center; }
@media (max-width: 768px) { .why-inner { grid-template-columns: 1fr; gap: 40px; } }
.why-text h2 { font-size: clamp(24px, 3vw, 36px); font-weight: 800; color: var(--primary); margin-bottom: 28px; }
.dark .why-text h2 { color: var(--accent-light); }
.why-list { list-style: none; margin-bottom: 36px; }
.why-item { display: flex; align-items: center; gap: 14px; padding: 10px 0; font-size: 16px; color: var(--text); border-bottom: 1px solid var(--border); }
.why-item:last-child { border-bottom: none; }
.why-check { color: var(--accent); flex-shrink: 0; }
.why-cta { margin-top: 8px; }
.why-img-wrap { border-radius: 20px; overflow: hidden; box-shadow: var(--shadow-lg); }
.why-img { width: 100%; height: 420px; object-fit: cover; }

/* ===== CTA ===== */
.cta-section {
  background: linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 50%, var(--accent) 100%);
  padding: 80px 24px;
  text-align: center;
}
.cta-inner { max-width: 700px; margin: 0 auto; }
.cta-inner h2 { color: #fff; font-size: clamp(24px, 3.5vw, 36px); font-weight: 800; margin-bottom: 16px; }
.cta-inner p { color: rgba(255,255,255,0.85); font-size: 17px; margin-bottom: 32px; }

/* ===== PAGE HERO ===== */
.page-hero { position: relative; height: 380px; display: flex; align-items: flex-end; overflow: hidden; }
.page-hero-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.parallax-img { will-change: transform; transform-origin: center center; }
.page-hero-overlay {
  position: absolute; inset: 0;
  background: linear-gradient(135deg, rgba(10,28,50,0.88) 0%, rgba(26,74,122,0.7) 100%);
}
.page-hero-content {
  position: relative; z-index: 2;
  max-width: 1200px; margin: 0 auto;
  padding: 0 24px 48px; width: 100%;
}
.page-hero-content h1 { color: var(--accent-light); font-size: clamp(28px, 4vw, 44px); font-weight: 900; margin-bottom: 12px; }
.page-hero-content p { color: var(--accent-light); font-size: 20px; max-width: 560px; }

/* ===== ABOUT ===== */
.mv-inner { display: grid; grid-template-columns: 1fr 1fr; gap: 28px; }
@media (max-width: 700px) { .mv-inner { grid-template-columns: 1fr; } }
.mv-card { background: var(--bg-card); border-radius: var(--radius); padding: 40px; box-shadow: var(--shadow); border: 1px solid var(--border); }
.mv-icon {
  width: 60px; height: 60px;
  background: linear-gradient(135deg, var(--primary), var(--accent));
  border-radius: 14px;
  display: flex; align-items: center; justify-content: center;
  color: #fff; margin-bottom: 20px;
}
.mv-card h2 { font-size: 22px; font-weight: 800; color: var(--primary); margin-bottom: 14px; }
.dark .mv-card h2 { color: var(--accent-light); }
.mv-card p { color: var(--text-muted); line-height: 1.8; font-size: 15px; }

.stats-section {
  background: linear-gradient(135deg, var(--primary-dark), var(--primary), #1e5fa0);
  padding: 64px 24px;
}
.stats-inner { max-width: 1100px; margin: 0 auto; text-align: center; }
.stats-title { color: #fff; font-size: 28px; font-weight: 800; margin-bottom: 48px; }
.stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 32px; }
.stat-item { display: flex; flex-direction: column; align-items: center; gap: 8px; }
.stat-num { font-size: 52px; font-weight: 900; color: var(--accent-light); line-height: 1; }
.stat-label { color: rgba(255,255,255,0.85); font-size: 16px; font-weight: 500; }

.values-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 24px; }
.value-card {
  background: var(--bg-card);
  border-radius: var(--radius); padding: 32px;
  box-shadow: var(--shadow); border: 1px solid var(--border);
  transition: transform 0.25s;
}
.value-card:hover { transform: translateY(-5px); }
.value-icon {
  width: 60px; height: 60px;
  border-radius: 14px;
  background: linear-gradient(135deg, var(--accent), var(--accent-light));
  display: flex; align-items: center; justify-content: center;
  color: #fff; margin-bottom: 18px;
}
.value-card h3 { font-size: 18px; font-weight: 700; margin-bottom: 10px; color: var(--text); }
.value-card p { color: var(--text-muted); font-size: 14px; line-height: 1.7; }

.team-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 28px; }
.team-card {
  background: var(--bg-card); border-radius: var(--radius);
  overflow: hidden; box-shadow: var(--shadow); text-align: center;
  transition: transform 0.25s, box-shadow 0.25s;
}
.team-card:hover { transform: translateY(-6px); box-shadow: var(--shadow-lg); }
.team-img-wrap { height: 240px; overflow: hidden; }
.team-img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.4s; }
.team-card:hover .team-img { transform: scale(1.05); }
.team-info { padding: 20px; }
.team-info h3 { font-size: 18px; font-weight: 700; color: var(--text); margin-bottom: 6px; }
.team-info p { color: var(--accent); font-size: 14px; font-weight: 500; }

/* ===== ABOUT HERO KICKER ===== */
.hero-kicker {
  display: inline-block;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--accent-light);
  margin-bottom: 16px;
}

/* ===== STORY SECTION ===== */
.story-section { background: var(--bg-section); }
.story-inner {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  align-items: center;
}
@media (max-width: 800px) { .story-inner { grid-template-columns: 1fr; gap: 32px; } }
.story-text h2 { font-size: clamp(26px, 3.5vw, 36px); font-weight: 800; color: var(--primary); margin-bottom: 20px; }
.dark .story-text h2 { color: var(--accent-light); }
.story-text p { color: var(--text-muted); font-size: 16px; line-height: 1.8; margin-bottom: 28px; }
.story-highlights { display: flex; flex-direction: column; gap: 14px; }
.highlight-item {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--text);
  font-size: 15px;
  font-weight: 500;
}
.highlight-item svg { color: var(--accent); flex-shrink: 0; }
.story-visual { position: relative; }
.story-image-wrap {
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border);
  aspect-ratio: 4 / 5;
}
.story-image { width: 100%; height: 100%; object-fit: cover; display: block; }
.story-badge {
  position: absolute;
  bottom: -14px;
  inset-inline-start: 24px;
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 50px;
  padding: 10px 18px;
  box-shadow: var(--shadow);
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
}
.story-badge svg { color: var(--accent); flex-shrink: 0; }

/* ===== MISSION & VISION ENHANCED ===== */
.mv-tag {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--accent);
  margin-bottom: 10px;
  display: block;
}
.mv-card h2 { font-size: clamp(22px, 3vw, 28px); font-weight: 800; color: var(--primary); margin-bottom: 14px; }
.dark .mv-card h2 { color: var(--accent-light); }
.mv-card p { color: var(--text-muted); line-height: 1.8; font-size: 15px; }

/* ===== STATS ENHANCED ===== */
.stat-card { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 24px 16px; border-radius: 20px; background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.2); backdrop-filter: blur(8px); }
.stat-card .stat-num-wrap { display: flex; align-items: baseline; gap: 4px; margin-bottom: 4px; }
.stat-num { font-size: 48px; font-weight: 900; color: var(--accent-light); line-height: 1; }
.stat-suffix { font-size: 24px; font-weight: 700; color: var(--accent-light); opacity: 0.9; }
.stat-label { color: rgba(255,255,255,0.85); font-size: 15px; font-weight: 500; }

/* ===== VALUES ENHANCED ===== */
.value-card { position: relative; overflow: hidden; }
.value-card::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: linear-gradient(135deg, var(--primary), var(--accent));
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 0;
}
.value-card:hover::before { opacity: 0.95; }
.value-card > * { position: relative; z-index: 1; }
.value-card:hover .value-icon,
.value-card:hover h3,
.value-card:hover p { color: #fff !important; }
.value-icon {
  width: 64px; height: 64px;
  border-radius: 16px;
  background: linear-gradient(135deg, var(--accent), var(--accent-light));
  display: flex; align-items: center; justify-content: center;
  color: #fff; margin-bottom: 18px;
  box-shadow: 0 8px 24px rgba(232,119,34,0.3);
}
.value-card h3 { font-size: 19px; font-weight: 700; margin-bottom: 10px; color: var(--text); }
.value-card p { color: var(--text-muted); font-size: 14.5px; line-height: 1.75; }

/* ===== TEAM SECTION ENHANCED ===== */
.team-img-wrap {
  position: relative;
  height: 200px;
  border-radius: 16px;
  overflow: hidden;
  background: var(--bg-section);
  margin-bottom: 16px;
}
.team-img-placeholder {
  width: 100%; height: 100%;
  background: linear-gradient(135deg, var(--border), var(--bg-section));
  display: flex; align-items: center; justify-content: center;
  color: var(--text-light);
  font-size: 13px; font-weight: 600;
}
.team-bio { display: block; color: var(--text-muted); font-size: 14px; line-height: 1.6; margin-top: 10px; }

/* ===== ARTICLES ===== */
.articles-hero { height: 360px; }
.articles-filter-wrap {
  background: var(--bg-card);
  border-bottom: 1px solid var(--border);
  padding: 20px 24px;
  position: sticky;
  top: var(--navbar-h);
  z-index: 10;
  box-shadow: var(--shadow);
}
.articles-filter { max-width: 1200px; margin: 0 auto; display: flex; gap: 10px; flex-wrap: wrap; }
.filter-btn {
  padding: 8px 20px;
  border-radius: 50px;
  border: 1.5px solid var(--border);
  background: transparent;
  color: var(--text-muted);
  font-size: 14px; font-weight: 500;
  cursor: pointer; transition: all 0.2s;
}
.filter-btn:hover { border-color: var(--accent); color: var(--accent); }
.filter-btn.active { background: var(--accent); border-color: var(--accent); color: #fff; }
.articles-section { padding: 60px 0; }
.articles-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); gap: 28px; }
.article-card {
  background: var(--bg-card); border-radius: var(--radius);
  overflow: hidden; box-shadow: var(--shadow); border: 1px solid var(--border);
  cursor: pointer; transition: transform 0.25s, box-shadow 0.25s;
}
.article-card:hover { transform: translateY(-6px); box-shadow: var(--shadow-lg); }
.article-img-wrap { position: relative; height: 220px; overflow: hidden; }
.article-img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.4s; }
.article-card:hover .article-img { transform: scale(1.06); }
.article-category-badge {
  position: absolute; top: 14px; inset-inline-start: 14px;
  background: var(--accent); color: #fff;
  font-size: 12px; font-weight: 600;
  padding: 4px 12px; border-radius: 50px;
}
.article-content { padding: 24px; }
.article-meta { display: flex; gap: 16px; margin-bottom: 12px; flex-wrap: wrap; }
.article-meta-item { display: flex; align-items: center; gap: 5px; color: var(--text-muted); font-size: 13px; }
.article-title { font-size: 18px; font-weight: 700; color: var(--text); margin-bottom: 10px; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.article-excerpt { color: var(--text-muted); font-size: 14px; line-height: 1.7; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; margin-bottom: 16px; }
.article-footer { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px; }
.article-date { color: var(--text-light); font-size: 13px; }
.article-read-more {
  display: flex; align-items: center; gap: 5px;
  color: var(--accent); font-size: 14px; font-weight: 600;
  background: transparent; border: none; cursor: pointer; padding: 0;
  transition: gap 0.2s;
}
.article-read-more:hover { gap: 10px; }

/* ===== ARTICLE DETAIL ===== */
.article-detail-hero { position: relative; height: 460px; display: flex; align-items: flex-end; overflow: hidden; }
.article-detail-hero-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.article-detail-overlay { position: absolute; inset: 0; background: linear-gradient(to top, rgba(5,15,30,0.92) 0%, rgba(5,15,30,0.5) 50%, transparent 100%); }
.article-detail-hero-content { position: relative; z-index: 2; max-width: 860px; margin: 0 auto; padding: 0 24px 48px; width: 100%; }
.back-btn {
  display: inline-flex; align-items: center; gap: 6px;
  color: rgba(255,255,255,0.8); background: rgba(255,255,255,0.1);
  border: none; border-radius: 8px; padding: 8px 16px;
  font-size: 14px; cursor: pointer; margin-bottom: 24px; transition: background 0.2s;
}
.back-btn:hover { background: rgba(255,255,255,0.2); color: #fff; }
.article-detail-title { color: #fff; font-size: clamp(22px, 3.5vw, 38px); font-weight: 900; line-height: 1.3; margin-bottom: 16px; }
.article-detail-meta { display: flex; gap: 20px; flex-wrap: wrap; color: rgba(255,255,255,0.75); font-size: 14px; }
.article-detail-meta span { display: flex; align-items: center; gap: 6px; }
.article-detail-body { max-width: 860px; margin: 0 auto; padding: 56px 24px; }
.article-detail-content { margin-bottom: 40px; }
.article-body-h3 { font-size: 20px; font-weight: 700; color: var(--primary); margin: 28px 0 12px; }
.dark .article-body-h3 { color: var(--accent-light); }
.article-body-p { color: var(--text); font-size: 16px; line-height: 1.9; margin-bottom: 14px; }
.article-body-li {
  color: var(--text); font-size: 16px; line-height: 1.9;
  padding: 4px 0; padding-inline-start: 20px;
  list-style-type: none; position: relative;
}
.article-body-li::before { content: '•'; color: var(--accent); position: absolute; inset-inline-start: 0; font-weight: 900; }
.article-body-li.check::before { display: none; }
.article-share {
  display: flex; align-items: center; gap: 10px;
  color: var(--text-muted); font-size: 14px;
  padding: 20px 0;
  border-top: 1px solid var(--border); border-bottom: 1px solid var(--border);
  margin-bottom: 40px;
}
.related-articles { margin-top: 40px; }
.related-articles h2 { font-size: 22px; font-weight: 800; color: var(--text); margin-bottom: 20px; }
.related-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 20px; }
.related-card { border-radius: var(--radius-sm); overflow: hidden; cursor: pointer; border: 1px solid var(--border); transition: transform 0.2s; background: var(--bg-card); }
.related-card:hover { transform: translateY(-4px); }
.related-img { width: 100%; height: 130px; object-fit: cover; }
.related-info { padding: 14px; }
.related-info h4 { font-size: 14px; font-weight: 600; color: var(--text); margin-bottom: 6px; line-height: 1.4; }
.related-info span { font-size: 12px; color: var(--text-muted); }

/* ===== NOT FOUND ===== */
.not-found { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 60vh; gap: 20px; padding: 24px; }
.not-found h2 { font-size: 24px; color: var(--text); }

/* ===== CONTACT ===== */
.contact-inner { display: grid; grid-template-columns: 1fr 1.4fr; gap: 48px; align-items: start; }
@media (max-width: 900px) { .contact-inner { grid-template-columns: 1fr; } }
.contact-info h2, .contact-form-wrap h2 { font-size: 24px; font-weight: 800; color: var(--primary); margin-bottom: 28px; }
.dark .contact-info h2, .dark .contact-form-wrap h2 { color: var(--accent-light); }
.contact-items { display: flex; flex-direction: column; gap: 16px; margin-bottom: 28px; }
.contact-item {
  display: flex; align-items: flex-start; gap: 16px;
  padding: 18px; background: var(--bg-card);
  border-radius: var(--radius-sm); border: 1px solid var(--border);
  text-decoration: none; color: inherit;
  transition: box-shadow 0.2s, transform 0.2s;
}
.contact-item:hover { box-shadow: var(--shadow); transform: translateX(-4px); }
[dir="rtl"] .contact-item:hover { transform: translateX(4px); }
.contact-item-icon {
  width: 48px; height: 48px; border-radius: 12px;
  background: linear-gradient(135deg, var(--primary), var(--accent));
  display: flex; align-items: center; justify-content: center;
  color: #fff; flex-shrink: 0;
}
.contact-item-text { display: flex; flex-direction: column; gap: 4px; flex: 1; }
.contact-item-label {
  font-size: 11px; color: var(--text-muted); font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 2px;
}
.contact-item-value {
  font-size: 14px; color: var(--text); font-weight: 600; line-height: 1.5;
}
.contact-item-value + .contact-item-value {
  border-top: 1px dashed var(--border);
  padding-top: 4px;
  margin-top: 2px;
}
.contact-map { border-radius: var(--radius-sm); overflow: hidden; }
.contact-form { display: flex; flex-direction: column; gap: 18px; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
@media (max-width: 600px) { .form-row { grid-template-columns: 1fr; } }
.form-group { display: flex; flex-direction: column; gap: 8px; }
.form-group label { font-size: 14px; font-weight: 600; color: var(--text); }
.form-group input, .form-group select, .form-group textarea {
  padding: 12px 16px;
  border-radius: var(--radius-sm); border: 1.5px solid var(--border);
  background: var(--bg); color: var(--text);
  font-size: 15px; outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  width: 100%; font-family: inherit;
}
.form-group input:focus, .form-group select:focus, .form-group textarea:focus {
  border-color: var(--accent); box-shadow: 0 0 0 3px rgba(232,119,34,0.15);
}
.form-group textarea { resize: vertical; }
.form-group select { appearance: none; cursor: pointer; }
.form-submit { align-self: flex-start; min-width: 180px; }
.form-success {
  display: flex; align-items: center; gap: 10px;
  background: #e8f7ee; color: #1a6b34;
  padding: 14px 20px; border-radius: var(--radius-sm);
  border: 1px solid #a3d9b7;
  font-size: 15px; font-weight: 500; margin-bottom: 16px;
}
.dark .form-success { background: #0d2a1a; color: #5de89a; border-color: #1a5033; }

/* ===== FOOTER ===== */
.footer { background: #06111e; color: rgba(255,255,255,0.82); padding: 60px 0 0; }
.footer-inner {
  max-width: 1200px; margin: 0 auto; padding: 0 24px 48px;
  display: grid; grid-template-columns: 2fr 1fr 1fr 1.5fr; gap: 40px;
}
@media (max-width: 900px) { .footer-inner { grid-template-columns: 1fr 1fr; } }
@media (max-width: 560px) { .footer-inner { grid-template-columns: 1fr; } }
.footer-logo { display: flex; align-items: center; gap: 12px; text-decoration: none; color: #fff; font-size: 17px; font-weight: 700; margin-bottom: 16px; }
.footer-logo-img { width: 44px; height: 44px; object-fit: contain; border-radius: 8px; }
.footer-tagline { color: rgba(255,255,255,0.55); font-size: 14px; line-height: 1.7; margin-bottom: 20px; }

.social-icon {
  width: 38px; height: 38px; border-radius: 10px;
  background: rgba(255,255,255,0.08);
  display: flex; align-items: center; justify-content: center;
  color: rgba(255,255,255,0.7);
  transition: background 0.2s, color 0.2s; text-decoration: none;
}
.social-icon:hover { background: var(--accent); color: #fff; }

.footer-links h4, .footer-services h4, .footer-contact h4 {
  color: #fff; font-size: 15px; font-weight: 700;
  margin-bottom: 18px; padding-bottom: 10px;
  border-bottom: 2px solid var(--accent); display: inline-block;
}
.footer-links ul, .footer-services ul, .footer-contact ul { list-style: none; display: flex; flex-direction: column; gap: 10px; }
.footer-links a { color: rgba(255,255,255,0.65); text-decoration: none; font-size: 14px; transition: color 0.2s; }
.footer-links a:hover { color: var(--accent-light); }
.footer-services li { color: rgba(255,255,255,0.65); font-size: 14px; }
.footer-services a { color: rgba(255,255,255,0.65); text-decoration: none; font-size: 14px; transition: color 0.2s; }
.footer-services a:hover { color: var(--accent-light); }
.footer-contact-item { display: flex; align-items: center; gap: 10px; color: rgba(255,255,255,0.65); text-decoration: none; font-size: 14px; transition: color 0.2s; }
.footer-contact-item:hover { color: var(--accent-light); }
.footer-bottom { border-top: 1px solid rgba(255,255,255,0.08); padding: 20px 24px; text-align: center; font-size: 13px; color: rgba(255,255,255,0.4); }

/* ===== CHATBOT ===== */
.chatbot-overlay {
  position: fixed; inset: 0; z-index: 1100;
  display: flex; align-items: flex-end; justify-content: flex-end;
  padding: 24px; pointer-events: none;
}
.chatbot-window {
  width: 380px; max-height: 560px;
  background: var(--bg-card); border-radius: 20px;
  box-shadow: var(--shadow-lg); border: 1px solid var(--border);
  display: flex; flex-direction: column; overflow: hidden;
  pointer-events: all;
  animation: slideUp 0.3s ease;
}
@keyframes slideUp {
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}
@media (max-width: 450px) {
  .chatbot-overlay { padding: 0; align-items: flex-end; }
  .chatbot-window { width: 100%; border-radius: 20px 20px 0 0; max-height: 70vh; }
}
.chatbot-header {
  background: linear-gradient(135deg, var(--primary), var(--primary-light));
  padding: 18px 20px;
  display: flex; align-items: center; justify-content: space-between;
  flex-shrink: 0;
}
.chatbot-title { display: flex; align-items: center; gap: 10px; color: #fff; font-size: 16px; font-weight: 700; }
.chatbot-close {
  background: rgba(255,255,255,0.15); border: none; border-radius: 8px;
  color: #fff; cursor: pointer; padding: 6px;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.2s;
}
.chatbot-close:hover { background: rgba(255,255,255,0.25); }
.chatbot-body { flex: 1; overflow-y: auto; padding: 16px; display: flex; flex-direction: column; gap: 12px; }
.chat-msg { display: flex; gap: 8px; align-items: flex-start; }
.chat-msg.bot { flex-direction: row; }
.chat-msg.user { flex-direction: row-reverse; }
.chat-avatar { width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.chat-msg.bot .chat-avatar { background: var(--primary); color: #fff; }
.chat-msg.user .chat-avatar { background: var(--accent); color: #fff; }
.chat-bubble { max-width: 78%; padding: 10px 14px; border-radius: 14px; font-size: 14px; line-height: 1.65; }
.chat-msg.bot .chat-bubble { background: var(--bg-section); color: var(--text); border-bottom-start-radius: 4px; }
.chat-msg.user .chat-bubble { background: var(--accent); color: #fff; border-bottom-end-radius: 4px; }
.quick-replies { padding: 8px 0; display: flex; flex-direction: column; gap: 7px; }
.quick-label { font-size: 12px; color: var(--text-muted); margin-bottom: 4px; }
.quick-reply-btn {
  background: var(--bg); border: 1.5px solid var(--border);
  border-radius: 20px; padding: 8px 14px;
  font-size: 13px; text-align: start; cursor: pointer;
  color: var(--text); transition: all 0.2s; font-family: inherit;
}
.quick-reply-btn:hover { border-color: var(--accent); color: var(--accent); background: rgba(232,119,34,0.05); }
.chatbot-footer { padding: 14px 16px; border-top: 1px solid var(--border); display: flex; gap: 10px; flex-shrink: 0; }
.chat-input {
  flex: 1; padding: 10px 14px; border-radius: 20px;
  border: 1.5px solid var(--border); background: var(--bg);
  color: var(--text); font-size: 14px; outline: none; font-family: inherit;
  transition: border-color 0.2s;
}
.chat-input:focus { border-color: var(--accent); }
.chat-send-btn {
  width: 40px; height: 40px; border-radius: 50%;
  background: var(--accent); border: none; color: #fff;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; flex-shrink: 0; transition: background 0.2s;
}
.chat-send-btn:hover { background: var(--accent-dark); }

/* ===== SCROLL ANIMATIONS ===== */
.anim-child {
  opacity: 0;
  transform: translateY(32px);
  transition: opacity 0.55s ease, transform 0.55s ease;
}
.anim-child.anim-visible {
  opacity: 1;
  transform: translateY(0);
}
.anim-fade {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.6s ease, transform 0.6s ease;
}
.anim-fade.anim-visible {
  opacity: 1;
  transform: translateY(0);
}
.page-hero {
  opacity: 0;
  transform: scale(1.03);
  transition: opacity 0.7s ease, transform 0.7s ease;
}
.page-hero.anim-visible {
  opacity: 1;
  transform: scale(1);
}

/* ===== HOME HERO ANIMATION ===== */
.hero-section {
  opacity: 0;
  transition: opacity 0.8s ease;
}
.hero-section.anim-visible { opacity: 1; }

/* Hero dots */
.hero-dots {
  display: flex;
  gap: 10px;
  margin-top: 28px;
  justify-content: flex-start;
}
[dir="rtl"] .hero-dots { justify-content: flex-start; }
.hero-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.6);
  background: transparent;
  cursor: pointer;
  padding: 0;
  transition: background 0.3s, border-color 0.3s, transform 0.3s;
}
.hero-dot:hover { border-color: #fff; transform: scale(1.2); }
.hero-dot-active { background: var(--accent); border-color: var(--accent); transform: scale(1.15); }

/* ===== CTA SECTION ANIMATION ===== */
.cta-section {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.6s ease, transform 0.6s ease;
}
.cta-section.anim-visible { opacity: 1; transform: translateY(0); }

/* ===== CONTACT PAGE (no-form layout) ===== */
.contact-info-only {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 48px;
  max-width: 860px;
  margin: 0 auto;
}
.contact-items-centered {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  width: 100%;
}
@media (max-width: 600px) {
  .contact-items-centered { grid-template-columns: 1fr; }
}


/* ===== GLOWING ORANGE LOCATION BADGE ===== */
.contact-map-section {
  width: 100%;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: var(--shadow-lg);
}
.contact-map-section.anim-fade {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.65s ease, transform 0.65s ease;
}
.contact-map-section.anim-fade.anim-visible {
  opacity: 1;
  transform: translateY(0);
}
.location-badge-wrap {
  background: var(--bg-card);
  border-radius: 0 0 20px 20px;
  padding: 20px 28px;
  display: flex;
  align-items: center;
  gap: 20px;
  border-top: 1px solid var(--border);
}
.location-badge {
  position: relative;
  width: 64px;
  height: 64px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.location-ring {
  position: absolute;
  border-radius: 50%;
  background: rgba(232,119,34,0.15);
  animation: locationPulse 2.4s ease-out infinite;
}
.location-ring-1 { width: 64px; height: 64px; animation-delay: 0s; }
.location-ring-2 { width: 50px; height: 50px; animation-delay: 0.4s; background: rgba(232,119,34,0.22); }
.location-ring-3 { width: 38px; height: 38px; animation-delay: 0.8s; background: rgba(232,119,34,0.30); }
@keyframes locationPulse {
  0%   { transform: scale(0.85); opacity: 0.9; }
  60%  { transform: scale(1.15); opacity: 0.4; }
  100% { transform: scale(1.30); opacity: 0; }
}
.location-pin-circle {
  position: relative;
  z-index: 2;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--accent), var(--accent-light));
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  box-shadow: 0 0 18px rgba(232,119,34,0.65), 0 4px 16px rgba(232,119,34,0.40);
}
.location-badge-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.location-badge-text strong {
  font-size: 0.95rem;
  color: var(--text);
  font-weight: 700;
}
.location-badge-text span {
  font-size: 0.85rem;
  color: var(--text-muted);
}
.contact-cta {
  width: 100%;
  background: linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%);
  border-radius: 20px;
  padding: 48px 40px;
  text-align: center;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}
.contact-cta-icon {
  width: 72px;
  height: 72px;
  background: rgba(255,255,255,0.12);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #25D366;
  margin-bottom: 4px;
}
.contact-cta h2 { font-size: 1.6rem; color: #fff; }
.contact-cta p { color: rgba(255,255,255,0.75); font-size: 1rem; }
.contact-cta-btns {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 8px;
}
.btn-whatsapp-big {
  background: #25D366;
  color: #fff;
  padding: 14px 32px;
  border-radius: 99px;
  font-weight: 700;
  font-size: 1rem;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  transition: background 0.2s, transform 0.2s;
}
.btn-whatsapp-big:hover { background: #1ebe5a; transform: translateY(-2px); }
.btn-outline-contact {
  background: transparent;
  color: #fff;
  border: 2px solid rgba(255,255,255,0.45);
  padding: 14px 32px;
  border-radius: 99px;
  font-weight: 600;
  font-size: 1rem;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  transition: background 0.2s, border-color 0.2s, transform 0.2s;
}
.btn-outline-contact:hover {
  background: rgba(255,255,255,0.12);
  border-color: #fff;
  transform: translateY(-2px);
}

/* ===== LOADING SCREEN ===== */
.loading-screen {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: linear-gradient(135deg, #0f2d4f 0%, #1a4a7a 60%, #0f1923 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.5s ease, transform 0.5s ease;
}
.loading-screen.fade-out {
  opacity: 0;
  transform: scale(1.04);
  pointer-events: none;
}
.loading-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 28px;
  animation: loadingAppear 0.6s ease;
}
@keyframes loadingAppear {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}
.loading-logo {
  width: 130px;
  height: 130px;
  object-fit: contain;
  filter: drop-shadow(0 8px 32px rgba(232,119,34,0.45));
  animation: logoPulse 1.4s ease-in-out infinite alternate;
}
@keyframes logoPulse {
  from { transform: scale(1);   filter: drop-shadow(0 8px 28px rgba(232,119,34,0.40)); }
  to   { transform: scale(1.07); filter: drop-shadow(0 12px 40px rgba(232,119,34,0.70)); }
}
.loading-bar-wrap {
  width: 200px;
  height: 4px;
  background: rgba(255,255,255,0.12);
  border-radius: 99px;
  overflow: hidden;
}
.loading-bar {
  height: 100%;
  width: 0%;
  background: linear-gradient(90deg, var(--accent), var(--accent-light));
  border-radius: 99px;
  animation: loadBar 1.6s ease forwards;
}
@keyframes loadBar {
  0%   { width: 0%; }
  40%  { width: 55%; }
  100% { width: 100%; }
}
.loading-text {
  color: rgba(255,255,255,0.80);
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 1.5px;
}

/* ===== SKELETON LOADER ===== */
.skeleton-loader {
  display: flex;
  flex-direction: column;
  gap: 40px;
  padding: 80px 24px;
  max-width: 1000px;
  margin: 0 auto;
}
.skeleton {
  background: linear-gradient(90deg, var(--bg-section) 25%, rgba(255,255,255,0.6) 50%, var(--bg-section) 75%);
  background-size: 200% 100%;
  animation: skeletonShimmer 1.6s ease-in-out infinite;
  border-radius: 8px;
  height: 16px;
}
.skeleton-wide { width: 100%; height: 22px; }
.skeleton-medium { width: 60%; }
.skeleton-button { width: 140px; height: 44px; border-radius: 50px; }
.skeleton-btn-lg { width: 180px; height: 48px; border-radius: 50px; }
.skeleton-img { width: 100%; height: 320px; border-radius: 20px; }
.skeleton-tagline { width: 30%; height: 14px; margin: 12px 0 8px; }
.skeleton-title { width: 55%; height: 32px; margin-bottom: 10px; }
.skeleton-desc { width: 80%; height: 20px; margin-bottom: 16px; opacity: 0.9; }
.skeleton-header { width: 40%; height: 24px; margin-bottom: 16px; border-radius: 6px; }
.skeleton-card { height: 140px; border-radius: 14px; }

.skeleton-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}
.skeleton-nav-links {
  display: flex;
  gap: 16px;
}
.skeleton-hero {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  align-items: center;
}
@media (max-width: 768px) {
  .skeleton-hero { grid-template-columns: 1fr; }
}
.skeleton-sections {
  display: grid;
  gap: 30px;
}
.skeleton-section {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.skeleton-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

@keyframes skeletonShimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Hide old loading styles (kept for reference, unused) */
.loading-screen, .shalil-splash2 { display: none !important; }

/* ===== WHATSAPP SUPPORT BUTTON (inside chat) ===== */
.chat-whatsapp-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0 16px 12px;
  padding: 10px 16px;
  background: #25D366;
  color: #fff;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  transition: background 0.2s, transform 0.2s;
  flex-shrink: 0;
}
.chat-whatsapp-btn:hover { background: #1da851; transform: translateY(-1px); }

/* ===== FLOATING BUTTONS GROUP ===== */
.float-btns {
  position: fixed;
  bottom: 28px;
  inset-inline-end: 28px;
  z-index: 1050;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

/* Chat / Assistant float button */
.float-chat-btn {
  width: 54px;
  height: 54px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  background: var(--primary);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 18px rgba(26,74,122,0.45);
  transition: transform 0.22s, box-shadow 0.22s, background 0.2s;
}
.float-chat-btn:hover {
  transform: scale(1.12);
  background: var(--primary-light);
  box-shadow: 0 8px 28px rgba(26,74,122,0.55);
}

/* WhatsApp float button */
.whatsapp-float {
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: #25D366;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(37,211,102,0.45);
  text-decoration: none;
  transition: transform 0.22s, box-shadow 0.22s;
  animation: waPulse 2.5s infinite;
}
.whatsapp-float:hover {
  transform: scale(1.12);
  box-shadow: 0 8px 32px rgba(37,211,102,0.6);
  animation: none;
}
@keyframes waPulse {
  0%, 100% { box-shadow: 0 4px 20px rgba(37,211,102,0.45); }
  50% { box-shadow: 0 4px 32px rgba(37,211,102,0.7), 0 0 0 10px rgba(37,211,102,0.12); }
}

/* ===== SHALIL CINEMATIC SPLASH ===== */
.shalil-splash2 {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: #050e1a;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  transition: opacity 0.6s ease;
}
.shalil-splash2--enter { opacity: 0; }
.shalil-splash2--hold  { opacity: 1; }
.shalil-splash2--leave { opacity: 0; pointer-events: none; }

/* Image cards */
.splash-img-wrap {
  position: absolute;
  width: 220px;
  height: 300px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,0.7);
  transition: transform 0.7s cubic-bezier(0.22,1,0.36,1), opacity 0.7s ease;
  opacity: 0;
}
.shalil-splash2--hold .splash-img-wrap,
.shalil-splash2--leave .splash-img-wrap { opacity: 1; }

.splash-img-wrap--left {
  left: 6vw;
  top: 50%;
  transform: translateY(-50%) translateX(-120%) rotate(-8deg);
}
.shalil-splash2--hold .splash-img-wrap--left,
.shalil-splash2--leave .splash-img-wrap--left {
  transform: translateY(-50%) translateX(0) rotate(-5deg);
}

.splash-img-wrap--right {
  right: 6vw;
  top: 50%;
  transform: translateY(-50%) translateX(120%) rotate(8deg);
}
.shalil-splash2--hold .splash-img-wrap--right,
.shalil-splash2--leave .splash-img-wrap--right {
  transform: translateY(-50%) translateX(0) rotate(5deg);
}

.splash-img-wrap--bottom {
  bottom: 4vh;
  left: 50%;
  transform: translateX(-50%) translateY(120%) rotate(-3deg);
  width: 280px;
  height: 180px;
}
.shalil-splash2--hold .splash-img-wrap--bottom,
.shalil-splash2--leave .splash-img-wrap--bottom {
  transform: translateX(-50%) translateY(0) rotate(-3deg);
}

.splash-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.splash-img-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, transparent 40%, rgba(5,14,26,0.55) 100%);
}

/* Center text */
.splash-center {
  position: relative;
  z-index: 2;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  opacity: 0;
  transform: scale(0.7);
  transition: opacity 0.6s cubic-bezier(0.22,1,0.36,1) 0.15s,
              transform 0.6s cubic-bezier(0.22,1,0.36,1) 0.15s;
}
.shalil-splash2--hold .splash-center,
.shalil-splash2--leave .splash-center {
  opacity: 1;
  transform: scale(1);
}

.splash-word {
  font-family: 'Cairo', sans-serif;
  font-size: clamp(2.4rem, 7vw, 4rem);
  font-weight: 900;
  line-height: 1;
  background: linear-gradient(135deg, #fff 30%, var(--accent) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: 0.04em;
}
.splash-sub {
  font-family: 'Cairo', sans-serif;
  font-size: clamp(0.8rem, 2vw, 1rem);
  color: rgba(255,255,255,0.65);
  letter-spacing: 0.12em;
  font-weight: 500;
}
.splash-line {
  width: 50px;
  height: 3px;
  background: var(--accent);
  border-radius: 2px;
  margin-top: 4px;
  transform: scaleX(0);
  transition: transform 0.5s ease 0.5s;
}
.shalil-splash2--hold .splash-line { transform: scaleX(1); }

/* Orbit truck */
.splash-orbit-ring {
  position: absolute;
  top: 50%; left: 50%;
  width: 160px; height: 160px;
  margin: -80px 0 0 -80px;
  border-radius: 50%;
  border: 1.5px dashed rgba(249,115,22,0.35);
  animation: splashOrbit 2.8s linear infinite;
}
.splash-orbit-icon {
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  animation: splashCounterOrbit 2.8s linear infinite;
  filter: drop-shadow(0 0 6px rgba(249,115,22,0.7));
}
@keyframes splashOrbit {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
@keyframes splashCounterOrbit {
  from { transform: translateX(-50%) rotate(0deg); }
  to   { transform: translateX(-50%) rotate(-360deg); }
}

/* ===== HOME SEARCH SECTION ===== */
.home-search-section {
  background: var(--bg-card);
  border-bottom: 1px solid var(--border);
  padding: 28px 24px;
}
.home-search-inner {
  max-width: 680px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.home-search-label {
  font-size: 0.85rem;
  color: var(--text-muted);
  font-weight: 600;
  text-align: center;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  margin: 0;
}
.home-search-wrap {
  position: relative;
}
.home-search-box {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--bg);
  border: 1.5px solid var(--border);
  border-radius: 12px;
  padding: 0 14px;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.home-search-box:focus-within {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(26,74,122,0.12);
}
.home-search-icon { color: var(--text-muted); flex-shrink: 0; }
.home-search-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 1rem;
  color: var(--text);
  padding: 14px 0;
  font-family: 'Cairo', sans-serif;
}
.home-search-clear {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  padding: 4px;
  border-radius: 50%;
  transition: color 0.2s;
}
.home-search-clear:hover { color: var(--text); }

/* Dropdown results */
.home-search-results {
  position: absolute;
  top: calc(100% + 6px);
  left: 0; right: 0;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 12px;
  list-style: none;
  margin: 0; padding: 6px;
  z-index: 200;
  box-shadow: 0 12px 40px rgba(0,0,0,0.15);
  max-height: 320px;
  overflow-y: auto;
}
.home-search-result-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  padding: 10px 10px;
  border-radius: 8px;
  text-align: start;
  transition: background 0.15s;
}
.home-search-result-item:hover { background: var(--bg); }
.search-result-img {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
}
.search-result-title {
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text);
  line-height: 1.3;
}
.search-result-cat {
  display: block;
  font-size: 0.75rem;
  color: var(--accent);
  margin-top: 2px;
}
.search-result-arrow {
  margin-inline-start: auto;
  color: var(--text-muted);
  flex-shrink: 0;
}
.home-search-empty {
  position: absolute;
  top: calc(100% + 6px);
  left: 0; right: 0;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 16px;
  text-align: center;
  color: var(--text-muted);
  font-size: 0.9rem;
  box-shadow: 0 8px 24px rgba(0,0,0,0.1);
}

/* Quick links row */
.home-search-quick {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
}
.home-search-quick-label {
  font-size: 0.82rem;
  color: var(--text-muted);
}
.home-search-quick-btn {
  padding: 7px 18px;
  border-radius: 20px;
  border: 1.5px solid var(--primary);
  background: var(--primary);
  color: #fff;
  font-family: 'Cairo', sans-serif;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, transform 0.15s;
}
.home-search-quick-btn:hover { background: var(--primary-light); transform: translateY(-1px); }
.home-search-quick-btn--outline {
  background: transparent;
  color: var(--primary);
}
.home-search-quick-btn--outline:hover {
  background: var(--primary);
  color: #fff;
}

/* ===== SCROLL TO TOP BUTTON ===== */
.scroll-top-btn {
  position: fixed;
  inset-inline-start: 24px;
  bottom: 110px;
  z-index: 999;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  border: none;
  background: var(--accent);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 18px rgba(249,115,22,0.45);
  opacity: 0;
  transform: translateY(20px) scale(0.85);
  pointer-events: none;
  transition: opacity 0.3s, transform 0.3s, box-shadow 0.2s;
}
.scroll-top-btn--visible {
  opacity: 1;
  transform: translateY(0) scale(1);
  pointer-events: auto;
}
.scroll-top-btn:hover {
  box-shadow: 0 8px 28px rgba(249,115,22,0.6);
  transform: translateY(-3px) scale(1.08);
}

/* ===== FOOTER MAP (rectangular, full width) ===== */
.footer-map-section {
  padding: 28px 0 24px;
  border-top: 1px solid rgba(255,255,255,0.08);
}
.footer-map-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 12px;
}
.footer-map-title span {
  color: var(--accent-light);
}
.footer-map-frame {
  background: rgba(255,255,255,0.05);
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,0.08);
  box-shadow: var(--shadow);
}
.footer-map-frame iframe {
  border-radius: 14px;
  display: block;
}
.footer-map-label {
  color: rgba(255,255,255,0.7);
  font-size: 0.85rem;
  text-align: center;
  margin: 0;
  margin-top: 10px;
}

/* ===== CONTACT MAP (full width section) ===== */
.contact-map-full-section {
  width: 100%;
  background: var(--bg-section);
  padding: 40px 0;
}
.contact-map-section {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  box-sizing: border-box;
}
.contact-map-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--text);
  margin-bottom: 14px;
}
.contact-map-title span {
  color: var(--primary);
}
.branch-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  margin-top: 24px;
}
@media (min-width: 900px) {
  .branch-grid {
    grid-template-columns: 1fr 300px;
  }
}
.contact-map-full {
  grid-column: 1 / -1;
}
.contact-map-frame {
  background: var(--bg-card);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border);
  position: relative;
  height: 560px;
}
.map-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: var(--bg-section);
  z-index: 3;
  pointer-events: none;
  opacity: 1;
  transition: opacity 0.4s ease;
}
.map-placeholder.loaded {
  opacity: 0;
}
.map-placeholder-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--bg-card);
  color: var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(26,74,122,0.12);
}
.map-placeholder-text {
  color: var(--text-muted);
  font-size: 0.95rem;
  font-weight: 500;
  margin: 0;
}
.contact-map-frame iframe {
  border-radius: 20px;
  display: block;
  width: 100%;
  height: 100%;
}

/* Branch grid below map */
.branch-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 20px;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: var(--shadow);
  transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
}
.branch-card:hover {
  transform: translateY(-3px);
  box-shadow: var(--shadow-lg);
  border-color: var(--accent-light);
}
.branch-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: var(--bg-section);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.branch-info {
  flex: 1;
}
.branch-info strong {
  display: block;
  font-size: 1rem;
  font-weight: 700;
  color: var(--text);
}
.branch-info span {
  font-size: 0.9rem;
  color: var(--text-muted);
}
.branch-link {
  margin-left: 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--primary);
  text-decoration: none;
  padding: 8px 14px;
  border: 1px solid var(--primary);
  border-radius: 10px;
  transition: all 0.2s;
  white-space: nowrap;
}
.branch-link:hover {
  background: var(--primary);
  color: #fff;
  border-color: var(--primary);
}

/* ===== CONTACT GRID ===== */
.contact-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  margin-top: 24px;
}
@media (min-width: 900px) {
  .contact-grid {
    grid-template-columns: 1fr 1.1fr;
    gap: 40px;
  }
}
.contact-info-col {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.contact-form-col {
  display: flex;
  flex-direction: column;
}
.contact-form-wrap {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 32px;
  box-shadow: var(--shadow);
}
.form-status {
  padding: 20px;
  border-radius: 12px;
  background: rgba(37,211,102,0.1);
  border: 1px solid rgba(37,211,102,0.25);
  color: #25D366;
  font-weight: 600;
  text-align: center;
}
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
@media (max-width: 600px) {
  .form-row { grid-template-columns: 1fr; }
}
.form-group { display: flex; flex-direction: column; gap: 8px; }
.form-group label { font-size: 14px; font-weight: 600; color: var(--text); }
.form-group input, .form-group select, .form-group textarea {
  padding: 12px 16px;
  border-radius: var(--radius-sm);
  border: 1.5px solid var(--border);
  background: var(--bg); color: var(--text);
  font-size: 15px; outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  width: 100%; font-family: inherit;
}
.form-group input:focus, .form-group select:focus, .form-group textarea:focus {
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(232,119,34,0.15);
}
.form-group textarea { resize: vertical; }
.form-group select { appearance: none; cursor: pointer; }
.form-submit { align-self: flex-start; min-width: 180px; }

/* ===== CONTACT MAP PAGE ===== */
.contact-map-section-pg {
  width: 100%;
  background: var(--bg-section);
  padding: 40px 0;
}

/* ===== CLIENTS PAGE ===== */
.clients-page {}

.clients-section-header {
  text-align: center;
  margin-bottom: 48px;
}
.clients-section-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 18px;
  border-radius: 50px;
  font-size: 0.85rem;
  font-weight: 700;
  margin-bottom: 14px;
  letter-spacing: 0.02em;
}
.clients-badge-outside {
  background: rgba(37,99,168,0.12);
  color: var(--primary-light);
  border: 1px solid rgba(37,99,168,0.25);
}
.clients-badge-inside {
  background: rgba(232,119,34,0.12);
  color: var(--accent);
  border: 1px solid rgba(232,119,34,0.25);
}
.clients-section-title {
  font-size: clamp(1.6rem, 3vw, 2.2rem);
  font-weight: 800;
  color: var(--text);
  margin-bottom: 10px;
}
.clients-section-sub {
  color: var(--text-muted);
  font-size: 1.05rem;
  max-width: 520px;
  margin: 0 auto;
}

/* Outside Egypt */
.clients-outside-section { background: var(--bg-section); }
.clients-outside-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  max-width: 700px;
  margin: 0 auto;
}
.clients-country-card {
  background: var(--bg-card);
  border-radius: var(--radius);
  padding: 36px 28px;
  text-align: center;
  box-shadow: var(--shadow);
  border: 1px solid var(--border);
  transition: transform 0.22s, box-shadow 0.22s;
}
.clients-country-card:hover {
  transform: translateY(-5px);
  box-shadow: var(--shadow-lg);
}
.clients-country-flag {
  font-size: 3rem;
  margin-bottom: 14px;
  line-height: 1;
}
.clients-country-card h3 {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--text);
  margin-bottom: 8px;
}
.clients-country-card p {
  color: var(--text-muted);
  font-size: 0.92rem;
  line-height: 1.6;
}

/* Inside Egypt, Sectors */
.clients-inside-section { background: var(--bg); }
.clients-sectors {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 28px;
}
.clients-sector-card {
  background: var(--bg-card);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  border: 1px solid var(--border);
  overflow: hidden;
  transition: transform 0.22s, box-shadow 0.22s;
}
.clients-sector-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
}
.clients-sector-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  border-bottom: 3px solid;
  background: var(--bg-section);
}
.clients-sector-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}
.clients-sector-header h3 {
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.4;
}
.clients-sector-body {
  padding: 20px 24px;
}
.clients-subsector {
  margin-bottom: 18px;
}
.clients-subsector:last-child { margin-bottom: 0; }
.clients-subsector-name {
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 1px dashed var(--border);
}
.clients-company-list {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.clients-company-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.93rem;
  color: var(--text);
  line-height: 1.4;
}
.clients-company-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  opacity: 0.75;
}
.clients-company-with-logo {
  display: flex;
  align-items: center;
  gap: 10px;
}
.clients-company-logo {
  width: 24px;
  height: 24px;
  object-fit: contain;
  flex-shrink: 0;
  border-radius: 2px;
}
.clients-company-dot-hidden {
  display: none;
}
/* Sector card image */
.clients-sector-img-wrap {
  position: relative;
  height: 160px;
  overflow: hidden;
}
.clients-sector-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.5s ease;
}
.clients-sector-card:hover .clients-sector-img {
  transform: scale(1.06);
}
.clients-sector-img-overlay {
  position: absolute;
  inset: 0;
  opacity: 0.55;
}
.clients-sector-img-title {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px 18px;
  color: #fff;
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1.35;
  text-shadow: 0 1px 6px rgba(0,0,0,0.5);
  background: linear-gradient(to top, rgba(0,0,0,0.55), transparent);
}

/* Gulf card image */
.clients-gulf-card {
  background: var(--bg-card);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  border: 1px solid var(--border);
  overflow: hidden;
  max-width: 900px;
  margin: 0 auto;
}
.clients-gulf-img-wrap {
  position: relative;
  height: 200px;
  overflow: hidden;
}
.clients-gulf-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.5s ease;
}
.clients-gulf-card:hover .clients-gulf-img {
  transform: scale(1.04);
}
.clients-gulf-img-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(245,158,11,0.6), rgba(15,45,79,0.7));
}
.clients-gulf-img-label {
  position: absolute;
  bottom: 18px;
  left: 24px;
  right: 24px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #fff;
  font-size: 1.15rem;
  font-weight: 700;
  text-shadow: 0 2px 8px rgba(0,0,0,0.4);
}
.clients-gulf-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 8px 20px;
}
@media (max-width: 600px) {
  .clients-sectors { grid-template-columns: 1fr; }
  .clients-gulf-grid { grid-template-columns: 1fr; }
  .clients-sector-img-wrap { height: 120px; }
  .clients-gulf-img-wrap { height: 150px; }
}

/* ══════════════════════════════════════════
   Company Card, logo + name + specialty
   ══════════════════════════════════════════ */
.co-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 11px 14px;
  transition: transform 0.18s, box-shadow 0.18s;
}
.co-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow);
}
.co-card-logo-wrap {
  position: relative;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
}
.co-card-logo {
  width: 44px;
  height: 44px;
  object-fit: contain;
  border-radius: 8px;
  background: #fff;
  padding: 3px;
  border: 1px solid var(--border);
}
.co-card-initials {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.82rem;
  font-weight: 800;
  color: #fff;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}
.co-card-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.co-card-name {
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--text);
  line-height: 1.35;
  white-space: normal;
}
.co-card-specialty {
  font-size: 0.75rem;
  color: var(--text-muted);
  line-height: 1.3;
}
.co-card-location {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.72rem;
  color: var(--primary);
  font-weight: 600;
  margin-top: 2px;
}

/* ══════════════════════════════════════════
   Sector Block, header image + card grid
   ══════════════════════════════════════════ */
.sectors-list {
  display: flex;
  flex-direction: column;
  gap: 36px;
}
.sector-block {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  overflow: hidden;
}
.sector-block-header {
  display: flex;
  align-items: center;
  gap: 0;
  border-left: 4px solid;
}
[dir="rtl"] .sector-block-header { border-left: none; border-right: 4px solid; }
.sector-block-img-wrap {
  position: relative;
  width: 120px;
  min-height: 72px;
  flex-shrink: 0;
  overflow: hidden;
}
.sector-block-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.sector-block-img-overlay {
  position: absolute;
  inset: 0;
  opacity: 0.5;
}
.sector-block-title-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  flex: 1;
}
.sector-block-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.sector-block-title {
  font-size: 1.05rem;
  font-weight: 800;
  flex: 1;
  line-height: 1.3;
}
.sector-block-count {
  font-size: 0.72rem;
  font-weight: 700;
  color: #fff;
  padding: 2px 9px;
  border-radius: 20px;
  white-space: nowrap;
}
.sector-block-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 10px;
  padding: 16px;
  border-top: 1px solid var(--border);
  background: var(--bg-section);
}

/* ══════════════════════════════════════════
   Gulf Block
   ══════════════════════════════════════════ */
.gulf-block {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  overflow: hidden;
}
.gulf-block-hero-wrap {
  position: relative;
  height: 200px;
  overflow: hidden;
}
.gulf-block-hero-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.gulf-block-hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(8,145,178,0.65), rgba(15,45,79,0.75));
}
.gulf-block-hero-label {
  position: absolute;
  bottom: 20px;
  left: 28px;
  right: 28px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #fff;
  font-size: 1.25rem;
  font-weight: 800;
  text-shadow: 0 2px 8px rgba(0,0,0,0.4);
}
.gulf-companies-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 10px;
  padding: 18px;
  background: var(--bg-section);
}

@media (max-width: 700px) {
  .sector-block-grid { grid-template-columns: 1fr; }
  .gulf-companies-grid { grid-template-columns: 1fr; }
  .sector-block-img-wrap { width: 80px; min-height: 60px; }
  .gulf-block-hero-wrap { height: 140px; }
  .co-card-logo-wrap, .co-card-logo, .co-card-initials { width: 36px; height: 36px; }
}
/* ===== NEW ABOUT DESIGN ===== */
.about-hero { position: relative; background: linear-gradient(135deg, var(--primary) 0%, #0e2540 100%); color: #fff; padding: 120px 24px 100px; text-align: center; overflow: hidden; }
.about-hero-inner { max-width: 900px; margin: 0 auto; z-index: 2; position: relative; }
.about-hero-logo-wrap { display: inline-flex; align-items: center; gap: 16px; margin-bottom: 32px; }
.about-hero-logo { width: 64px; height: 64px; object-fit: contain; filter: drop-shadow(0 8px 24px rgba(0,0,0,0.35)); }
.about-hero-word { font-family: Georgia, serif; font-weight: 700; font-size: 2rem; letter-spacing: 0.02em; line-height: 1; }
.about-hero-main { color: #eeb603; font-size: 2.8rem; display: block; }
.about-hero-sub { font-size: 1.1rem; opacity: 0.85; font-weight: 400; }
.about-hero-title { font-family: Georgia, serif; font-size: clamp(2rem, 5vw, 3.6rem); line-height: 1.15; margin: 12px 0 18px; letter-spacing: -0.03em; }
.about-hero-desc { font-size: 1.15rem; opacity: 0.85; max-width: 640px; margin: 0 auto; line-height: 1.65; }

.about-block { background: var(--bg); padding: 80px 24px; }
.about-block-inner { max-width: 1000px; margin: 0 auto; }
.about-block-label { font-family: Georgia, serif; font-size: 0.85rem; letter-spacing: 0.15em; text-transform: uppercase; color: var(--accent); margin-bottom: 6px; }
.about-block-title { font-family: Georgia, serif; font-size: clamp(1.8rem, 4vw, 2.8rem); letter-spacing: -0.03em; margin-bottom: 40px; line-height: 1.2; }

.about-story-text { font-size: 1.15rem; line-height: 1.75; color: var(--text); max-width: 780px; }

.about-step-grid { display: grid; gap: 32px; }
.about-step { display: flex; gap: 24px; align-items: flex-start; padding: 24px 0; border-bottom: 1px solid var(--border); }
.about-step--right { flex-direction: row-reverse; text-align: right; }
.about-step-icon-wrap { flex-shrink: 0; width: 56px; height: 56px; border-radius: 16px; background: linear-gradient(135deg, var(--primary), #0e2540); color: #fff; display: grid; place-items: center; box-shadow: 0 8px 24px rgba(26,74,122,0.2); }
.about-step-content strong { font-family: Georgia, serif; font-size: 1.15rem; display: block; margin-bottom: 6px; }
.about-step-content p { color: var(--text-soft); font-size: 0.95rem; line-height: 1.65; }

.about-stats { background: linear-gradient(135deg, var(--bg-card), var(--bg-section)); padding: 60px 24px; }
.about-stat-row { max-width: 1000px; margin: 0 auto; display: grid; grid-template-columns: repeat(auto-fit,minmax(140px,1fr)); gap: 20px; }
.about-stat { background: var(--bg); border: 1px solid var(--border); border-radius: 16px; padding: 28px 18px; text-align: center; transition: transform .2s; }
.about-stat:hover { transform: translateY(-4px); }
.about-stat--alt { background: var(--primary); color: #fff; border-color: var(--primary); }
.about-stat-num { font-family: Georgia, serif; font-size: 2rem; font-weight: 700; color: var(--primary); }
.about-stat--alt .about-stat-num { color: #eeb603; }
.about-stat-desc { font-size: 0.85rem; opacity: 0.8; margin-top: 4px; }

.about-values-grid { display: grid; gap: 24px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
.about-value { background: var(--bg-card); border: 1px solid var(--border); border-radius: 16px; padding: 28px 24px; transition: box-shadow .2s; }
.about-value:hover { box-shadow: 0 12px 40px rgba(26,74,122,0.08); }
.about-value-icon { width: 48px; height: 48px; border-radius: 12px; background: linear-gradient(135deg, var(--primary), #0e2540); color: #fff; display: grid; place-items: center; margin-bottom: 14px; }
.about-value strong { display: block; font-family: Georgia, serif; font-size: 1.05rem; margin-bottom: 6px; }
.about-value p { font-size: 0.9rem; color: var(--text-soft); line-height: 1.5; }

.about-cta { padding: 60px 24px; text-align: center; background: linear-gradient(135deg, var(--primary) 0%, #0e2540 100%); }
.about-cta-btn { display: inline-block; background: #eeb603; color: #0e2540; padding: 14px 36px; border-radius: 50px; font-weight: 700; text-decoration: none; transition: transform .15s; }
.about-cta-btn:hover { transform: translateY(-3px); }
/* ===== CONTACT PAGE UI ===== */
.contact-hero {
  background: linear-gradient(135deg, var(--primary) 0%, #0e2540 100%);
  color: #fff;
  padding: 100px 24px 70px;
  text-align: center;
}
.contact-hero-inner { max-width: 720px; margin: 0 auto; }
.contact-hero-title { font-family: Georgia, serif; font-size: clamp(2rem, 5vw, 3rem); line-height: 1.2; margin-bottom: 14px; letter-spacing: -0.02em; }
.contact-hero-desc { font-size: 1.15rem; opacity: 0.9; line-height: 1.6; margin: 0; }

.contact-section { padding: 70px 24px 100px; background: var(--bg-section); }
.contact-inner { max-width: 1100px; margin: 0 auto; display: grid; grid-template-columns: 1fr; gap: 48px; }
@media (min-width: 900px) { .contact-inner { grid-template-columns: 1fr 1.15fr; gap: 56px; } }

.contact-info-col { display: flex; flex-direction: column; gap: 20px; }
.contact-info-title { font-family: Georgia, serif; font-size: 1.6rem; margin: 0 0 8px; letter-spacing: -0.02em; }
.contact-info-desc { margin: 0; color: var(--text-muted); font-size: 1rem; line-height: 1.6; }
.contact-cards { display: flex; flex-direction: column; gap: 14px; margin-top: 10px; }
.contact-card { display: flex; gap: 16px; align-items: flex-start; background: var(--bg-card); border: 1px solid var(--border); border-radius: 14px; padding: 18px 20px; transition: box-shadow .2s, border-color .2s; cursor: pointer; min-height: 72px; }
.contact-card:hover { box-shadow: var(--shadow); border-color: var(--accent); }
.contact-card-icon { flex-shrink: 0; width: 44px; height: 44px; border-radius: 12px; background: linear-gradient(135deg, var(--primary), #0e2540); color: #fff; display: grid; place-items: center; font-size: 20px; }
.contact-card-body { min-width: 0; }
.contact-card-label { font-size: 0.85rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--text-muted); display: block; margin-bottom: 4px; }
.contact-card-value { display: block; font-size: 1rem; color: var(--text); text-decoration: none; line-height: 1.5; }
.contact-card-value:hover { color: var(--accent); text-decoration: underline; }

.contact-form-col { display: flex; flex-direction: column; gap: 16px; }
.contact-form-wrap { background: var(--bg-card); border: 1px solid var(--border); border-radius: 16px; padding: 32px 28px; box-shadow: var(--shadow); }
.contact-form-title { font-family: Georgia, serif; font-size: 1.7rem; margin: 0 0 24px; letter-spacing: -0.02em; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
@media (max-width: 600px) { .form-row { grid-template-columns: 1fr; } }
.form-group { display: flex; flex-direction: column; gap: 8px; }
.form-label { display: flex; align-items: baseline; font-size: 0.9rem; font-weight: 700; color: var(--text); gap: 6px; }
.form-required { color: #ef4444; font-size: 1rem; line-height: 1; }
.form-input, .form-select, .form-textarea {
  width: 100%; padding: 12px 16px; border-radius: var(--radius-sm);
  border: 1.5px solid var(--border); background: var(--bg); color: var(--text);
  font-size: 15px; font-family: inherit; outline: none; transition: border-color .2s, box-shadow .2s;
}
.form-select { cursor: pointer; appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%235a6a7e' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 12px center; padding-right: 36px; }
.form-input:focus, .form-select:focus, .form-textarea:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(232,119,34,0.15); }
.form-input-error { border-color: #ef4444; }
.form-textarea { resize: vertical; min-height: 110px; }
.form-error { font-size: 0.8rem; color: #ef4444; margin: 0; }
.form-status { padding: 18px; border-radius: 12px; background: rgba(37,211,102,0.1); border: 1px solid rgba(37,211,102,0.25); color: #25D366; font-weight: 700; text-align: center; font-size: 0.95rem; }
.form-submit { align-self: flex-start; min-width: 170px; margin-top: 4px; }

.contact-map-section { padding: 70px 24px 100px; background: var(--bg); }
.contact-map-inner { max-width: 1100px; margin: 0 auto; }
.contact-map-title { display: flex; align-items: center; gap: 10px; font-family: Georgia, serif; font-size: 1.6rem; margin: 0 0 20px; letter-spacing: -0.02em; color: var(--text); }
.contact-map-frame { position: relative; border-radius: 20px; overflow: hidden; border: 1px solid var(--border); min-height: 560px; background: var(--bg-section); margin-bottom: 20px; }
.map-placeholder { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; color: var(--text-muted); background: var(--bg-section); transition: opacity .3s; }
.map-placeholder.loaded { opacity: 0; pointer-events: none; }
.map-placeholder-icon { width: 48px; height: 48px; border-radius: 14px; background: linear-gradient(135deg, var(--primary), #0e2540); color: #fff; display: grid; place-items: center; font-size: 24px; }
.map-placeholder-text { font-size: 0.95rem; }
.contact-map-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 14px; }
.contact-map-card { display: flex; gap: 12px; align-items: center; background: var(--bg-card); border: 1px solid var(--border); border-radius: 14px; padding: 14px 16px; transition: box-shadow .2s; }
.contact-map-card:hover { box-shadow: var(--shadow); }
.contact-map-card-icon { flex-shrink: 0; width: 36px; height: 36px; border-radius: 10px; background: var(--bg-section); color: var(--primary); display: grid; place-items: center; font-size: 16px; }
.contact-map-card-body { min-width: 0; }
.contact-map-card-body strong { display: block; font-size: 0.95rem; color: var(--text); }
.contact-map-card-body span { font-size: 0.85rem; color: var(--text-muted); }
.contact-map-card-link { margin-left: auto; font-size: 0.85rem; font-weight: 700; color: var(--accent); text-decoration: none; display: flex; align-items: center; gap: 6px; }
.contact-map-card-link:hover { text-decoration: underline; }

.contact-cta { padding: 70px 24px; text-align: center; background: linear-gradient(135deg, var(--primary) 0%, #0e2540 100%); color: #fff; }
.contact-cta-inner { max-width: 720px; margin: 0 auto; display: flex; flex-direction: column; align-items: center; gap: 18px; }
.contact-cta-icon { width: 64px; height: 64px; border-radius: 16px; background: rgba(255,255,255,0.1); color: #eeb603; display: grid; place-items: center; font-size: 32px; }
.contact-cta-text h2 { font-family: Georgia, serif; font-size: clamp(1.6rem, 4vw, 2.2rem); margin: 0; letter-spacing: -0.02em; }
.contact-cta-text p { margin: 0; opacity: 0.9; font-size: 1rem; line-height: 1.6; }
.contact-cta-btns { display: flex; gap: 14px; justify-content: center; margin-top: 8px; }
.btn-whatsapp-big { background: #25D366; color: #fff; padding: 14px 28px; border-radius: 50px; font-weight: 700; text-decoration: none; display: inline-flex; align-items: center; gap: 10px; transition: transform .15s; }
.btn-whatsapp-big:hover { transform: translateY(-3px); filter: brightness(1.1); }
.btn-outline-contact { background: transparent; color: #fff; border: 2px solid rgba(255,255,255,0.5); padding: 14px 28px; border-radius: 50px; font-weight: 700; text-decoration: none; display: inline-flex; align-items: center; gap: 10px; transition: transform .15s, border-color .2s; }
.btn-outline-contact:hover { transform: translateY(-3px); border-color: #fff; }

/* ===== REFACTORING UI DESIGN SYSTEM COMPONENTS =====
   Logo-derived navy palette, grayscale-first hierarchy,
   constrained spacing/type scales, depth via shadows.
*/

/* Typography scale */
.rf-eyebrow {
  font-size: 12px; font-weight: 700; letter-spacing: 0.16em;
  text-transform: uppercase; color: var(--navy-500); margin-bottom: var(--space-2);
}
.rf-heading { font-size: clamp(24px, 4vw, 40px); line-height: 1.15; font-weight: 800; color: var(--ink-dark); letter-spacing: -0.02em; margin: 0 0 var(--space-4); }
.rf-lead { font-size: 17px; line-height: 1.7; color: var(--ink-base); max-width: 62ch; margin: 0; }
.rf-body { font-size: 15px; line-height: 1.65; color: var(--ink-base); }

/* Section */
.rf-section { padding: var(--space-16) var(--space-6); }
.rf-section-inner { max-width: 1100px; margin: 0 auto; }
.rf-section:nth-child(even) { background: var(--bg-section); }

/* Hero */
.rf-hero { background: linear-gradient(135deg, var(--navy-500) 0%, var(--navy-700) 100%); color: #fff; padding: var(--space-16) var(--space-6) var(--space-12); text-align: center; position: relative; overflow: hidden; }
.rf-hero-inner { max-width: 700px; margin: 0 auto; position: relative; z-index: 1; }
.rf-hero-logo { display: inline-flex; align-items: center; gap: var(--space-3); margin-bottom: var(--space-6); }
.rf-hero-logo-img { width: 48px; height: 48px; object-fit: contain; filter: drop-shadow(0 8px 20px rgba(0,0,0,0.35)); }
.rf-hero-logo-text { line-height: 1.15; }
.rf-hero-logo-main { display: block; font-family: Georgia, serif; font-size: 2.2rem; font-weight: 700; color: #eeb603; letter-spacing: -0.02em; }
.rf-hero-logo-sub { display: block; font-size: 13px; font-weight: 500; color: rgba(255,255,255,0.85); letter-spacing: 0.08em; text-transform: uppercase; }
.rf-hero-title { font-family: Georgia, serif; font-size: clamp(28px, 6vw, 48px); line-height: 1.1; font-weight: 800; color: #fff; margin: 0 0 var(--space-5); letter-spacing: -0.03em; }
.rf-hero-sub { font-size: 18px; line-height: 1.7; color: rgba(255,255,255,0.9); max-width: 60ch; margin: 0 auto var(--space-8); }
.rf-hero-cta { display: inline-block; background: #eeb603; color: var(--navy-700); padding: var(--space-3) var(--space-8); border-radius: 50px; font-weight: 800; text-decoration: none; font-size: 16px; transition: transform .15s, box-shadow .2s; box-shadow: var(--shadow-md); }
.rf-hero-cta:hover { transform: translateY(-3px); box-shadow: var(--shadow-lg); }

/* Cards */
.rf-card-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: var(--space-5); }
.rf-card { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius); padding: var(--space-5) var(--space-5) var(--space-4); display: flex; gap: var(--space-4); align-items: flex-start; transition: box-shadow .2s, border-color .2s, transform .1s; }
.rf-card:hover { box-shadow: var(--shadow-lg); border-color: var(--navy-300); transform: translateY(-2px); }
.rf-card-icon { flex-shrink: 0; width: 44px; height: 44px; border-radius: 12px; background: linear-gradient(135deg, var(--navy-500), var(--navy-600)); color: #fff; display: grid; place-items: center; font-size: 20px; box-shadow: var(--shadow-sm); }
.rf-card-body { min-width: 0; }
.rf-card-label { font-size: 12px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: var(--ink-muted); display: block; margin-bottom: var(--space-2); }
.rf-card-link { display: block; font-size: 15px; color: var(--ink-base); text-decoration: none; line-height: 1.55; }
.rf-card-link:hover { color: var(--navy-500); text-decoration: underline; }

/* Steps */
.rf-step-grid { display: grid; gap: var(--space-8); }
.rf-step { display: flex; gap: var(--space-5); align-items: flex-start; padding: var(--space-6) 0; border-bottom: 1px solid var(--border); }
.rf-step--right { flex-direction: row-reverse; }
.rf-step-badge { flex-shrink: 0; width: 36px; height: 36px; border-radius: 10px; background: var(--navy-100); color: var(--navy-500); font-weight: 800; font-size: 15px; display: grid; place-items: center; }
.rf-step-icon { flex-shrink: 0; width: 44px; height: 44px; border-radius: 12px; background: linear-gradient(135deg, var(--navy-500), var(--navy-600)); color: #fff; display: grid; place-items: center; font-size: 20px; box-shadow: var(--shadow-sm); }
.rf-step-body { min-width: 0; }
.rf-step-title { display: block; font-family: Georgia, serif; font-size: 17px; color: var(--ink-dark); margin-bottom: var(--space-2); letter-spacing: -0.01em; }
.rf-step-desc { font-size: 14.5px; line-height: 1.6; color: var(--ink-base); margin: 0; }

/* Stats */
.rf-stat-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: var(--space-5); }
.rf-stat { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius); padding: var(--space-6) var(--space-4); text-align: center; transition: box-shadow .2s; }
.rf-stat:hover { box-shadow: var(--shadow-md); }
.rf-stat-num { font-family: Georgia, serif; font-size: 30px; font-weight: 800; color: var(--navy-500); line-height: 1.1; }
.rf-stat-label { font-size: 13px; font-weight: 600; color: var(--ink-muted); margin-top: var(--space-2); letter-spacing: 0.02em; }

/* Values */
.rf-values-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: var(--space-5); }
.rf-value { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius); padding: var(--space-6) var(--space-5); transition: box-shadow .2s; }
.rf-value:hover { box-shadow: var(--shadow-md); }
.rf-value-icon { width: 44px; height: 44px; border-radius: 12px; background: linear-gradient(135deg, var(--navy-500), var(--navy-600)); color: #fff; display: grid; place-items: center; font-size: 20px; margin-bottom: var(--space-3); box-shadow: var(--shadow-sm); }
.rf-value-title { display: block; font-family: Georgia, serif; font-size: 16px; color: var(--ink-dark); margin-bottom: var(--space-2); }
.rf-value-desc { font-size: 14px; line-height: 1.6; color: var(--ink-base); margin: 0; }

/* CTA */
.rf-cta { padding: var(--space-16) var(--space-6); text-align: center; background: linear-gradient(135deg, var(--navy-500) 0%, var(--navy-700) 100%); color: #fff; }
.rf-cta-inner { max-width: 720px; margin: 0 auto; display: flex; flex-direction: column; align-items: center; gap: var(--space-4); }
.rf-cta-icon { width: 64px; height: 64px; border-radius: 18px; background: rgba(238,119,34,0.15); color: var(--accent); display: grid; place-items: center; font-size: 32px; box-shadow: var(--shadow-md); }
.rf-cta-title { font-family: Georgia, serif; font-size: clamp(24px, 5vw, 34px); line-height: 1.2; color: #fff; margin: 0; letter-spacing: -0.02em; }
.rf-cta-sub { font-size: 17px; line-height: 1.7; color: rgba(255,255,255,0.9); margin: 0; max-width: 60ch; }
.rf-cta-btns { display: flex; gap: var(--space-3); justify-content: center; margin-top: var(--space-2); }
.rf-btn-whatsapp { background: #25D366; color: #fff; padding: var(--space-3) var(--space-8); border-radius: 50px; font-weight: 800; text-decoration: none; font-size: 16px; display: inline-flex; align-items: center; gap: var(--space-2); transition: transform .15s, filter .2s; box-shadow: var(--shadow-md); }
.rf-btn-whatsapp:hover { transform: translateY(-3px); filter: brightness(1.15); }
.rf-btn-call { background: transparent; color: #fff; border: 2px solid rgba(255,255,255,0.5); padding: var(--space-2-5) var(--space-8); border-radius: 50px; font-weight: 800; text-decoration: none; font-size: 16px; display: inline-flex; align-items: center; gap: var(--space-2); transition: transform .15s, border-color .2s; }
.rf-btn-call:hover { transform: translateY(-3px); border-color: #fff; }

/* Map */
.rf-map-section { padding: var(--space-16) var(--space-6); }
.rf-map-wrap { position: relative; border-radius: var(--radius-lg); overflow: hidden; border: 1px solid var(--border); min-height: 520px; background: var(--bg-section); margin-bottom: var(--space-8); }
.rf-map-placeholder { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: var(--space-3); color: var(--ink-muted); background: var(--bg-section); transition: opacity .3s; z-index: 2; }
.rf-map-placeholder.loaded { opacity: 0; pointer-events: none; }
.rf-map-placeholder-icon { width: 56px; height: 56px; border-radius: 16px; background: linear-gradient(135deg, var(--navy-500), var(--navy-600)); color: #fff; display: grid; place-items: center; font-size: 28px; box-shadow: var(--shadow-md); }
.rf-map-placeholder-text { font-size: 15px; }
.rf-branch-list { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: var(--space-4); }
.rf-branch-card { display: flex; gap: var(--space-3); align-items: center; background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius); padding: var(--space-4) var(--space-5); transition: box-shadow .2s; }
.rf-branch-card:hover { box-shadow: var(--shadow-md); }
.rf-branch-icon { flex-shrink: 0; width: 36px; height: 36px; border-radius: 10px; background: var(--bg-section); color: var(--navy-500); display: grid; place-items: center; font-size: 16px; }
.rf-branch-body { min-width: 0; }
.rf-branch-name { display: block; font-size: 15px; color: var(--ink-dark); font-weight: 700; }
.rf-branch-sub { font-size: 13px; color: var(--ink-muted); }
.rf-branch-link { margin-left: auto; font-size: 13px; font-weight: 700; color: var(--navy-500); text-decoration: none; display: flex; align-items: center; gap: var(--space-2); }
.rf-branch-link:hover { text-decoration: underline; }

/* Utilities */
.rf-inview { animation: rfFadeIn .6s ease both; }
@keyframes rfFadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }

/* Adjust btn padding var fallback */
.rf-btn-call { padding: calc(var(--space-2) + 1px) var(--space-8); }

/* Hide old unused about/contact classes gracefully (kept for safety) */
